import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["list", "counter", "selectedCount", "limitWarning", "saveWrapper", "saveBtn"]
  static values = { url: String, updateUrl: String, limit: Number }

  connect() {
    this.brands = []
    this.dirty = false
    this.fetchBrands()
  }

  async fetchBrands() {
    try {
      const res = await fetch(this.urlValue)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      this.brands = json.data?.brands || []
      this.renderGrid()
    } catch (e) {
      console.error("[brand-selection] Failed to load brands:", e)
      this.listTarget.innerHTML = `<div class="col-span-full px-3 py-6 text-center text-xs text-red-400">Failed to load brands</div>`
    }
  }

  renderGrid() {
    if (!this.brands.length) {
      this.listTarget.innerHTML = `<div class="col-span-full px-3 py-6 text-center text-xs text-gray-400 dark:text-gray-500">No pages or accounts found. Connect your Meta account first.</div>`
      return
    }

    const selectedCount = this.brands.filter(b => b.selected).length
    const atLimit = selectedCount >= this.limitValue

    const rows = this.brands.map(brand => {
      const isSelected = brand.selected
      const isLocked = !isSelected && atLimit
      const id = brand.type === "page" ? brand.fb_page_id : brand.fb_instagram_account_id
      const isIG = brand.type === "instagram"

      const borderClass = isSelected
        ? "ring-2 ring-brand bg-brand/5 dark:bg-brand/10 border-transparent"
        : isLocked
          ? "border-gray-200 dark:border-gray-800 opacity-50"
          : "border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-600"

      const cursorClass = isLocked ? "cursor-not-allowed" : "cursor-pointer"
      const action = isLocked ? "" : `data-action="click->brand-selection#toggle"`

      const icon = isIG ? this.igIcon() : this.fbIcon()

      const indicator = isSelected
        ? `<svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>`
        : isLocked
          ? `<svg class="w-5 h-5 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>`
          : `<div class="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600"></div>`

      const followers = brand.followers > 0
        ? `<span class="text-xs text-gray-400 dark:text-gray-500">${this.formatNumber(brand.followers)} followers</span>`
        : ""

      return `<div class="flex items-center gap-3 p-3 border rounded-lg transition-colors ${borderClass} ${cursorClass}"
                   ${action}
                   data-brand-id="${this.esc(id)}"
                   data-brand-type="${brand.type}"
                   data-fb-business-id="${this.esc(brand.fb_business_id || "")}"
                   data-fb-page-id="${this.esc(brand.fb_page_id || "")}">
        <div class="flex-shrink-0">${icon}</div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-900 dark:text-white truncate">${this.esc(brand.name)}</p>
          <div class="flex items-center gap-2">
            <span class="text-xs text-gray-500 dark:text-gray-400">${isIG ? "Instagram" : (brand.category || "Facebook Page")}</span>
            ${followers}
          </div>
        </div>
        <div class="flex-shrink-0">${indicator}</div>
      </div>`
    })

    this.listTarget.innerHTML = rows.join("")
    this.updateCounter()
  }

  toggle(event) {
    const card = event.currentTarget
    const brandId = card.dataset.brandId
    const brandType = card.dataset.brandType

    const brand = this.brands.find(b => {
      const id = b.type === "page" ? b.fb_page_id : b.fb_instagram_account_id
      return id === brandId && b.type === brandType
    })

    if (!brand) return

    const selectedCount = this.brands.filter(b => b.selected).length

    // Prevent selecting over limit
    if (!brand.selected && selectedCount >= this.limitValue) return

    brand.selected = !brand.selected
    this.dirty = true
    this.renderGrid()
    this.showSaveButton()
  }

  async save() {
    const selected = this.brands.filter(b => b.selected).map(b => ({
      type: b.type,
      id: b.type === "page" ? b.fb_page_id : b.fb_instagram_account_id,
      name: b.name,
      fb_business_id: b.fb_business_id,
      fb_page_id: b.fb_page_id
    }))

    if (this.hasSaveBtnTarget) {
      this.saveBtnTarget.disabled = true
      this.saveBtnTarget.textContent = "Saving..."
    }

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const res = await fetch(this.updateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({ brands: selected })
      })

      const json = await res.json()
      if (json.success) {
        this.dirty = false
        this.hideSaveButton()
        if (this.hasSaveBtnTarget) {
          this.saveBtnTarget.textContent = "Saved!"
          setTimeout(() => { this.saveBtnTarget.textContent = "Save Selection" }, 2000)
        }
      } else {
        alert(json.error || "Failed to save brands")
      }
    } catch (e) {
      console.error("[brand-selection] Save failed:", e)
      alert("Failed to save brand selections")
    } finally {
      if (this.hasSaveBtnTarget) {
        this.saveBtnTarget.disabled = false
      }
    }
  }

  updateCounter() {
    const selectedCount = this.brands.filter(b => b.selected).length
    const atLimit = selectedCount >= this.limitValue

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = selectedCount
    }

    if (this.hasLimitWarningTarget) {
      this.limitWarningTarget.classList.toggle("hidden", !atLimit)
    }
  }

  showSaveButton() {
    if (this.hasSaveWrapperTarget) {
      this.saveWrapperTarget.style.display = ""
    }
  }

  hideSaveButton() {
    if (this.hasSaveWrapperTarget) {
      this.saveWrapperTarget.style.display = "none"
    }
  }

  // --- Icons ---

  fbIcon() {
    return `<div class="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
      <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z"/>
      </svg>
    </div>`
  }

  igIcon() {
    return `<div class="w-8 h-8 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
      <svg class="w-4 h-4 text-pink-500 dark:text-pink-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z"/>
      </svg>
    </div>`
  }

  // --- Helpers ---

  formatNumber(n) {
    if (!n) return "0"
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
    return n.toString()
  }

  esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML
  }
}
